import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../App';
import { apiRequest } from '../api';

export default function AccountsPage() {
  const DEFAULT_GCP_SCANNER_SERVICE_ACCOUNT = 'scanner@sroik-platform.iam.gserviceaccount.com';
  const PROVIDER_LABELS = {
    aws: 'AWS',
    gcp: 'GCP',
    azure: 'AZURE',
    oracle: 'OCI',
    ibmcloud: 'IBMCLOUD',
  };

  const {
    authToken, currentUser,
    cloudAccounts, cloudAccountForm, setCloudAccountForm,
    cloudBootstrap, cloudBootstrapBusy, cloudBootstrapError,
    cloudSyncBusy, cloudSyncAllBusy, cloudSyncByAccount, cloudSyncLastByAccount, cloudSyncError, cloudSyncMessage,
    loadCloudBootstrap,
    handleCloudAccountCreate, handleSyncAllAccounts, handleSyncOneAccount,
    handleUpdateAccount, handleDeleteAccount, handleBulkImport, resetMessages,
  } = useApp();

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ role_arn: '', external_id: '' });
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [setupMode, setSetupMode] = useState('recommended');
  const [copyStatus, setCopyStatus] = useState('');
  const [bulkImportBusy, setBulkImportBusy] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const [bulkImportError, setBulkImportError] = useState('');
  const bulkFileRef = useRef(null);
  const [errorGuide, setErrorGuide] = useState({ open: false, account: null, issueCode: '', details: null });
  const [gcpTelemetryGuide, setGcpTelemetryGuide] = useState({ open: false, account: null, lastSync: null });
  const [tenantUsers, setTenantUsers] = useState([]);
  const [openAccessAccountId, setOpenAccessAccountId] = useState(null);
  const [membersByAccount, setMembersByAccount] = useState({});
  const [invitationsByAccount, setInvitationsByAccount] = useState({});
  const [accessBusyByAccount, setAccessBusyByAccount] = useState({});
  const [inviteBusyByAccount, setInviteBusyByAccount] = useState({});
  const [revokeBusyByInvitation, setRevokeBusyByInvitation] = useState({});
  const [resendBusyByInvitation, setResendBusyByInvitation] = useState({});
  const [inviteFormByAccount, setInviteFormByAccount] = useState({});
  const [accessErrorByAccount, setAccessErrorByAccount] = useState({});
  const [accessMessageByAccount, setAccessMessageByAccount] = useState({});
  const [memberBusyById, setMemberBusyById] = useState({});

  const canManageAccess = ['owner', 'admin', 'superadmin'].includes((currentUser?.role || '').toLowerCase());
  const canRemoveMembers = (currentUser?.role || '').toLowerCase() === 'owner';

  useEffect(() => {
    resetMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function loadTenantUsers() {
      if (!authToken || !canManageAccess) {
        setTenantUsers([]);
        return;
      }
      try {
        const users = await apiRequest('/api/v1/auth/users', {}, authToken);
        setTenantUsers(users || []);
      } catch {
        setTenantUsers([]);
      }
    }
    loadTenantUsers();
  }, [authToken, canManageAccess]);

  useEffect(() => {
    if (copyStatus) {
      const timer = setTimeout(() => setCopyStatus(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [copyStatus]);

  useEffect(() => {
    if (!showSetupModal) return;
    loadCloudBootstrap(cloudAccountForm.provider, cloudAccountForm.account_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSetupModal, cloudAccountForm.provider, cloudAccountForm.account_id]);

  useEffect(() => {
    if (!showSetupModal) return;
    if (cloudAccountForm.provider !== 'aws') return;
    if (!cloudBootstrap?.external_id) return;

    setCloudAccountForm((prev) => {
      if (prev.provider !== 'aws') return prev;
      if ((prev.external_id || '').trim()) return prev;
      return { ...prev, external_id: cloudBootstrap.external_id };
    });
  }, [showSetupModal, cloudAccountForm.provider, cloudBootstrap?.external_id, setCloudAccountForm]);

  useEffect(() => {
    if (!showSetupModal) return;
    if (cloudAccountForm.provider !== 'gcp') return;
    if (!cloudBootstrap?.gcp_service_account_email) return;

    setCloudAccountForm((prev) => {
      if (prev.provider !== 'gcp') return prev;
      if ((prev.role_arn || '').trim()) return prev;
      return { ...prev, role_arn: cloudBootstrap.gcp_service_account_email };
    });
  }, [showSetupModal, cloudAccountForm.provider, cloudBootstrap?.gcp_service_account_email, setCloudAccountForm]);

  function startEdit(account) {
    setEditingId(account.id);
    setEditForm({ role_arn: account.role_arn || '', external_id: account.external_id || '' });
  }

  async function submitEdit(e, accountId) {
    e.preventDefault();
    await handleUpdateAccount(accountId, editForm.role_arn, editForm.external_id);
    setEditingId(null);
  }

  async function submitGuidedSetup(e) {
    e.preventDefault();
    const created = await handleCloudAccountCreate(e);
    if (created) {
      setShowSetupModal(false);
      setSetupMode('recommended');
    }
  }

  function switchProvider(provider) {
    setCloudAccountForm({ provider, account_id: '', role_arn: '', external_id: '' });
    setSetupMode('recommended');
  }

  function buildRecommendedRoleArn() {
    if (cloudAccountForm.provider !== 'aws') return '';
    if (!cloudBootstrap?.role_name) return '';
    const accountId = cloudAccountForm.account_id.trim();
    if (accountId.length !== 12) return '';
    return `arn:aws:iam::${accountId}:role/${cloudBootstrap.role_name}`;
  }

  function applyRecommendedValues() {
    if (cloudAccountForm.provider === 'aws') {
      const nextRoleArn = buildRecommendedRoleArn();
      setCloudAccountForm((prev) => ({
        ...prev,
        role_arn: nextRoleArn || prev.role_arn,
        external_id: cloudBootstrap?.external_id || prev.external_id,
      }));
      return;
    }

    setCloudAccountForm((prev) => ({
      ...prev,
      role_arn: cloudBootstrap?.gcp_service_account_email || prev.role_arn,
      external_id: '',
    }));
  }

  function handleAccountIdChange(nextAccountIdRaw) {
    const nextAccountId = nextAccountIdRaw.trim();
    setCloudAccountForm((prev) => {
      const next = { ...prev, account_id: nextAccountIdRaw };
      if (prev.provider !== 'aws' || !cloudBootstrap?.role_name) {
        return next;
      }

      const prevAccountId = prev.account_id.trim();
      const prevRecommendedRoleArn = /^\d{12}$/.test(prevAccountId)
        ? `arn:aws:iam::${prevAccountId}:role/${cloudBootstrap.role_name}`
        : '';
      const nextRecommendedRoleArn = /^\d{12}$/.test(nextAccountId)
        ? `arn:aws:iam::${nextAccountId}:role/${cloudBootstrap.role_name}`
        : '';

      const currentRoleArn = (prev.role_arn || '').trim();
      const shouldAutoFill = !currentRoleArn || currentRoleArn === prevRecommendedRoleArn;
      if (shouldAutoFill) {
        next.role_arn = nextRecommendedRoleArn;
      }
      return next;
    });
  }

  function buildAutoFillUpdates() {
    const updates = {};
    const filledFields = [];

    if (cloudAccountForm.provider === 'aws') {
      const nextRoleArn = buildRecommendedRoleArn();
      if (!cloudAccountForm.role_arn.trim() && nextRoleArn) {
        updates.role_arn = nextRoleArn;
        filledFields.push('role_arn');
      }
      if (!cloudAccountForm.external_id.trim() && cloudBootstrap?.external_id) {
        updates.external_id = cloudBootstrap.external_id;
        filledFields.push('external_id');
      }
      return { updates, filledFields };
    }

    if (!cloudAccountForm.role_arn.trim() && cloudBootstrap?.gcp_service_account_email) {
      updates.role_arn = cloudBootstrap.gcp_service_account_email;
      filledFields.push('role_arn');
    }

    return { updates, filledFields };
  }

  function canAutoFillMissingFields() {
    return buildAutoFillUpdates().filledFields.length > 0;
  }

  function autoFillMissingFields() {
    const { updates, filledFields } = buildAutoFillUpdates();
    if (filledFields.length === 0) {
      setCopyStatus('No missing fields can be auto-filled yet.');
      return;
    }
    setCloudAccountForm((prev) => ({ ...prev, ...updates }));
    setCopyStatus(`Auto-filled missing fields: ${filledFields.join(', ')}.`);
  }

  async function handleCopy(label, text) {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus(`${label} copied.`);
    } catch {
      setCopyStatus(`Could not copy ${label.toLowerCase()}.`);
    }
  }

  function hasAwsTrustPrincipal() {
    return !!cloudBootstrap?.aws_trust_principal_arn;
  }

  function getProviderLabel(provider) {
    const key = (provider || '').trim().toLowerCase();
    return PROVIDER_LABELS[key] || key.toUpperCase() || 'CLOUD';
  }

  function credentialEditorMeta(provider) {
    const key = (provider || '').toLowerCase();
    if (key === 'aws') {
      return {
        primaryLabel: 'Role ARN',
        primaryPlaceholder: 'arn:aws:iam::123456789012:role/SroikReadOnly',
        primaryHint: 'Use an assumable IAM role ARN for this account.',
        externalIdEnabled: true,
        externalIdHint: 'External ID should match the value used in the role trust policy.',
      };
    }
    if (key === 'gcp') {
      return {
        primaryLabel: 'Service Account Email',
        primaryPlaceholder: 'scanner@project.iam.gserviceaccount.com',
        primaryHint: 'Service account used by scanner runtime for this project.',
        externalIdEnabled: false,
        externalIdHint: '',
      };
    }
    if (key === 'azure') {
      return {
        primaryLabel: 'Credential Hint',
        primaryPlaceholder: 'Azure subscription access uses backend env credentials',
        primaryHint: 'Optional note only. Azure auth is usually resolved from backend environment credentials.',
        externalIdEnabled: false,
        externalIdHint: '',
      };
    }
    if (key === 'oracle') {
      return {
        primaryLabel: 'Credential Hint',
        primaryPlaceholder: 'OCI config/profile or instance principal context',
        primaryHint: 'Optional note only. OCI auth is typically provided via OCI config/profile or instance principal.',
        externalIdEnabled: false,
        externalIdHint: '',
      };
    }
    return {
      primaryLabel: 'Credential Hint',
      primaryPlaceholder: 'Optional credential hint',
      primaryHint: 'Optional note only. Runtime credentials are resolved from backend configuration.',
      externalIdEnabled: false,
      externalIdHint: '',
    };
  }

  function buildCloudAccountPayloadPreview() {
    const provider = cloudAccountForm.provider;
    const accountId = cloudAccountForm.account_id.trim();
    const roleArn = cloudAccountForm.role_arn.trim();
    const externalId = cloudAccountForm.external_id.trim();

    const missing = [];
    if (!accountId) missing.push('account_id');
    if (provider === 'aws' && !roleArn) missing.push('role_arn');

    if (provider === 'aws') {
      return JSON.stringify(
        {
          provider,
          account_id: accountId || '<missing: account_id>',
          role_arn: roleArn || '<missing: role_arn>',
          external_id: externalId || cloudBootstrap?.external_id || '<recommended: use generated external id>',
          ready_to_connect: missing.length === 0,
          missing_fields: missing,
        },
        null,
        2,
      );
    }

    return JSON.stringify(
      {
        provider,
        account_id: accountId || '<missing: account_id>',
        ready_to_connect: missing.length === 0,
        missing_fields: missing,
      },
      null,
      2,
    );
  }

  function buildAwsTrustPolicy() {
    const trustPrincipal = cloudBootstrap?.aws_trust_principal_arn || '<platform-trust-principal-unavailable>';
    const externalId = cloudAccountForm.external_id.trim() || cloudBootstrap?.external_id || '<missing: external_id>';
    return JSON.stringify(
      {
        Version: '2012-10-17',
        Statement: [
          {
            Effect: 'Allow',
            Principal: { AWS: trustPrincipal },
            Action: 'sts:AssumeRole',
            Condition: {
              StringEquals: {
                'sts:ExternalId': externalId,
              },
            },
          },
        ],
      },
      null,
      2,
    );
  }

  function buildAwsBillingPolicy() {
    return JSON.stringify(
      {
        Version: '2012-10-17',
        Statement: [
          {
            Effect: 'Allow',
            Action: [
              'ce:Get*',
              'ce:List*',
              'aws-portal:ViewBilling',
              'aws-portal:ViewUsage',
              'aws-portal:ViewAccount',
              'budgets:ViewBudget',
              'budgets:Describe*',
              'cur:DescribeReportDefinitions',
              'pricing:GetProducts',
            ],
            Resource: '*',
          },
        ],
      },
      null,
      2,
    );
  }

  function buildGcpCommandsForProject(projectIdRaw) {
    const serviceAccountEmail = cloudBootstrap?.gcp_service_account_email || DEFAULT_GCP_SCANNER_SERVICE_ACCOUNT;
    const member = `serviceAccount:${serviceAccountEmail}`;
    const projectId = projectIdRaw || '<project-id>';
    return [
      `gcloud projects add-iam-policy-binding ${projectId} --member="${member}" --role="roles/browser"`,
      `gcloud projects add-iam-policy-binding ${projectId} --member="${member}" --role="roles/cloudasset.viewer"`,
      'gcloud beta billing accounts add-iam-policy-binding <billing-account-id> --member="' + member + '" --role="roles/billing.viewer"',
    ].join('\n');
  }

  function buildGcpCommands() {
    return buildGcpCommandsForProject(cloudAccountForm.account_id);
  }

  function buildAzureCommands() {
    const subscriptionId = cloudAccountForm.account_id || '<subscription-guid>';
    const assignee = '<service-principal-object-id-or-app-id>';
    const scope = `/subscriptions/${subscriptionId}`;
    return [
      `az role assignment create --assignee "${assignee}" --role "Reader" --scope "${scope}"`,
      `az role assignment create --assignee "${assignee}" --role "Monitoring Reader" --scope "${scope}"`,
      `az role assignment create --assignee "${assignee}" --role "Cost Management Reader" --scope "${scope}"`,
    ].join('\n');
  }

  function buildOracleCommands() {
    return [
      'Allow dynamic-group sroik-scanner to inspect compartments in tenancy',
      'Allow dynamic-group sroik-scanner to inspect instances in tenancy',
      'Allow dynamic-group sroik-scanner to inspect volumes in tenancy',
      'Allow dynamic-group sroik-scanner to read metrics in tenancy',
    ].join('\n');
  }

  function buildIbmCloudCommands() {
    return [
      'ibmcloud login --sso',
      'ibmcloud target -g <resource-group>',
      'ibmcloud iam service-id-create sroik-scanner -d "Sroik scanner identity"',
      'ibmcloud iam service-policy-create <service-id> --roles Viewer --service-name is',
      'ibmcloud iam service-policy-create <service-id> --roles Viewer --service-name billing',
    ].join('\n');
  }

  function describeIssue(issueCode) {
    if (!issueCode) {
      return { message: '', hint: '' };
    }

    if (issueCode === 'aws_role_arn_invalid_format') {
      return {
        message: 'AWS role ARN is invalid. It looks like a user ARN, not a role ARN.',
        hint: 'Use arn:aws:iam::<account-id>:role/<role-name> and update credentials.',
      };
    }

    if (issueCode.startsWith('aws_assume_role_failed:')) {
      return {
        message: `AWS could not assume the configured role. ${issueCode.replace('aws_assume_role_failed:', '').trim()}`,
        hint: 'Check role trust policy principal, ExternalId condition, and role ARN/account pairing.',
      };
    }

    if (issueCode === 'aws_no_credentials') {
      return {
        message: 'AWS credentials are not configured yet.',
        hint: 'Finish IAM setup and connect a cross-account read role, then run sync again.',
      };
    }

    if (issueCode.startsWith('aws_auth_failed:')) {
      return {
        message: `AWS authentication failed. ${issueCode.replace('aws_auth_failed:', '').trim()}`,
        hint: 'Verify temporary credentials/session validity and IAM permissions for STS.',
      };
    }

    if (issueCode.startsWith('aws_regions_discovery_failed:')) {
      return {
        message: `AWS region discovery failed. ${issueCode.replace('aws_regions_discovery_failed:', '').trim()}`,
        hint: 'Grant ec2:DescribeRegions to the scanner role.',
      };
    }

    if (issueCode.startsWith('aws_region_scan_failed:')) {
      return {
        message: `AWS region scan failed. ${issueCode.replace('aws_region_scan_failed:', '').trim()}`,
        hint: 'Confirm read permissions for EC2 and EBS APIs in the affected region.',
      };
    }

    if (issueCode === 'gcp_no_credentials') {
      return {
        message: 'GCP credentials are not configured yet.',
        hint: 'Configure ADC or Workload Identity for the backend runtime, then retry sync.',
      };
    }

    if (issueCode === 'gcp_project_id_missing') {
      return {
        message: 'GCP project ID is missing.',
        hint: 'Set a valid project ID when connecting the account.',
      };
    }

    if (issueCode === 'gcp_client_library_unavailable') {
      return {
        message: 'GCP client libraries are unavailable on the server.',
        hint: 'Install and deploy google-auth and google-api-python-client dependencies.',
      };
    }

    if (issueCode.startsWith('gcp_scan_failed:')) {
      return {
        message: `GCP scan failed. ${issueCode.replace('gcp_scan_failed:', '').trim()}`,
        hint: 'Check IAM roles (browser, cloudasset.viewer, billing.viewer) and API enablement.',
      };
    }

    if (issueCode === 'azure_no_credentials') {
      return {
        message: 'Azure credentials are not configured yet.',
        hint: 'Set AZURE_CLIENT_ID, AZURE_TENANT_ID, and AZURE_CLIENT_SECRET in backend runtime.',
      };
    }

    if (issueCode === 'azure_client_library_unavailable') {
      return {
        message: 'Azure client libraries are unavailable on the server.',
        hint: 'Install and deploy azure-identity, azure-mgmt-compute, and azure-mgmt-monitor.',
      };
    }

    if (issueCode.startsWith('azure_scan_failed:')) {
      return {
        message: `Azure scan failed. ${issueCode.replace('azure_scan_failed:', '').trim()}`,
        hint: 'Verify subscription access, Reader/Monitoring Reader permissions, and credential validity.',
      };
    }

    if (issueCode === 'oracle_no_credentials') {
      return {
        message: 'Oracle Cloud credentials are not configured yet.',
        hint: 'Configure OCI config/profile or run backend with OCI instance principals.',
      };
    }

    if (issueCode === 'oracle_client_library_unavailable') {
      return {
        message: 'Oracle Cloud client library is unavailable on the server.',
        hint: 'Install and deploy the OCI SDK dependency.',
      };
    }

    if (issueCode.startsWith('oracle_scan_failed:')) {
      return {
        message: `Oracle Cloud scan failed. ${issueCode.replace('oracle_scan_failed:', '').trim()}`,
        hint: 'Verify tenancy OCID, policies, and monitoring access for scanner identity.',
      };
    }

    if (issueCode === 'ibmcloud_no_credentials') {
      return {
        message: 'IBM Cloud credentials are not configured yet.',
        hint: 'Set IBM_CLOUD_APIKEY (and optionally IBM_CLOUD_REGION) in backend runtime.',
      };
    }

    if (issueCode === 'ibmcloud_client_library_unavailable') {
      return {
        message: 'IBM Cloud client libraries are unavailable on the server.',
        hint: 'Install and deploy ibm-cloud-sdk-core and ibm-vpc.',
      };
    }

    if (issueCode === 'ibmcloud_monitoring_not_configured') {
      return {
        message: 'IBM Cloud monitoring endpoint is not configured.',
        hint: 'Set IBM_MONITORING_PROM_URL to enable telemetry-based rightsizing.',
      };
    }

    if (issueCode.startsWith('ibmcloud_scan_failed:')) {
      return {
        message: `IBM Cloud scan failed. ${issueCode.replace('ibmcloud_scan_failed:', '').trim()}`,
        hint: 'Verify VPC viewer permissions, region, and API key access.',
      };
    }

    return { message: issueCode, hint: 'Review account credentials and IAM permissions, then retry sync.' };
  }

  function openErrorGuide(account, issueCode, details) {
    setErrorGuide({
      open: true,
      account,
      issueCode,
      details,
    });
  }

  function closeErrorGuide() {
    setErrorGuide({ open: false, account: null, issueCode: '', details: null });
  }

  function openGcpTelemetryGuide(account, lastSync) {
    setGcpTelemetryGuide({ open: true, account, lastSync: lastSync || null });
  }

  function closeGcpTelemetryGuide() {
    setGcpTelemetryGuide({ open: false, account: null, lastSync: null });
  }

  function openGuidedSetup(provider = 'aws') {
    switchProvider(provider);
    setSetupMode('recommended');
    setShowSetupModal(true);
    loadCloudBootstrap(provider, cloudAccountForm.account_id);
  }

  function setAccessBusy(accountId, busy) {
    setAccessBusyByAccount((prev) => ({ ...prev, [accountId]: busy }));
  }

  function setInviteBusy(accountId, busy) {
    setInviteBusyByAccount((prev) => ({ ...prev, [accountId]: busy }));
  }

  function clearAccessFeedback(accountId) {
    setAccessErrorByAccount((prev) => ({ ...prev, [accountId]: '' }));
    setAccessMessageByAccount((prev) => ({ ...prev, [accountId]: '' }));
  }

  function getInviteForm(accountId) {
    return inviteFormByAccount[accountId] || {
      invited_email: '',
      invited_role: 'viewer',
      expires_in_days: 7,
    };
  }

  function updateInviteForm(accountId, patch) {
    setInviteFormByAccount((prev) => ({
      ...prev,
      [accountId]: {
        ...getInviteForm(accountId),
        ...patch,
      },
    }));
  }

  async function loadAccountAccess(accountId) {
    if (!authToken || !accountId) return;
    setAccessBusy(accountId, true);
    clearAccessFeedback(accountId);
    try {
      const [members, invitations] = await Promise.all([
        apiRequest(`/api/v1/cloud-accounts/${accountId}/members`, {}, authToken),
        canManageAccess
          ? apiRequest(`/api/v1/cloud-accounts/${accountId}/invitations`, {}, authToken)
          : Promise.resolve([]),
      ]);
      setMembersByAccount((prev) => ({ ...prev, [accountId]: members || [] }));
      setInvitationsByAccount((prev) => ({ ...prev, [accountId]: invitations || [] }));
    } catch (err) {
      setAccessErrorByAccount((prev) => ({
        ...prev,
        [accountId]: err.message || 'Could not load access information.',
      }));
    } finally {
      setAccessBusy(accountId, false);
    }
  }

  async function toggleAccessPanel(accountId) {
    if (openAccessAccountId === accountId) {
      setOpenAccessAccountId(null);
      return;
    }
    setOpenAccessAccountId(accountId);
    await loadAccountAccess(accountId);
  }

  async function submitInvitation(e, accountId) {
    e.preventDefault();
    if (!canManageAccess) return;

    const form = getInviteForm(accountId);
    clearAccessFeedback(accountId);
    setInviteBusy(accountId, true);
    try {
      const response = await apiRequest(
        `/api/v1/cloud-accounts/${accountId}/invitations`,
        {
          method: 'POST',
          body: JSON.stringify({
            invited_email: form.invited_email,
            invited_role: form.invited_role,
            expires_in_days: Number(form.expires_in_days || 7),
          }),
        },
        authToken,
      );

      updateInviteForm(accountId, { invited_email: '', invited_role: 'viewer', expires_in_days: 7 });
      setAccessMessageByAccount((prev) => ({
        ...prev,
        [accountId]: `Invitation created for ${response.invitation.invited_email}. Token: ${response.invitation_token}`,
      }));
      await loadAccountAccess(accountId);
    } catch (err) {
      setAccessErrorByAccount((prev) => ({
        ...prev,
        [accountId]: err.message || 'Could not create invitation.',
      }));
    } finally {
      setInviteBusy(accountId, false);
    }
  }

  async function revokeInvitation(accountId, invitationId) {
    if (!canManageAccess) return;
    setRevokeBusyByInvitation((prev) => ({ ...prev, [invitationId]: true }));
    clearAccessFeedback(accountId);
    try {
      await apiRequest(
        `/api/v1/cloud-accounts/invitations/${invitationId}/revoke`,
        { method: 'POST' },
        authToken,
      );
      setAccessMessageByAccount((prev) => ({
        ...prev,
        [accountId]: 'Invitation revoked.',
      }));
      await loadAccountAccess(accountId);
    } catch (err) {
      setAccessErrorByAccount((prev) => ({
        ...prev,
        [accountId]: err.message || 'Could not revoke invitation.',
      }));
    } finally {
      setRevokeBusyByInvitation((prev) => {
        const next = { ...prev };
        delete next[invitationId];
        return next;
      });
    }
  }

  async function resendInvitation(accountId, invitationId, mode = 'resend') {
    if (!canManageAccess) return;
    setResendBusyByInvitation((prev) => ({ ...prev, [invitationId]: true }));
    clearAccessFeedback(accountId);
    try {
      const response = await apiRequest(
        `/api/v1/cloud-accounts/invitations/${invitationId}/resend`,
        { method: 'POST' },
        authToken,
      );
      setAccessMessageByAccount((prev) => ({
        ...prev,
        [accountId]: mode === 'reinvite'
          ? `Invitation re-issued for ${response.invitation.invited_email}. Token: ${response.invitation_token}`
          : `Invitation resent to ${response.invitation.invited_email}. Token: ${response.invitation_token}`,
      }));
      await loadAccountAccess(accountId);
    } catch (err) {
      setAccessErrorByAccount((prev) => ({
        ...prev,
        [accountId]: err.message || 'Could not resend invitation.',
      }));
    } finally {
      setResendBusyByInvitation((prev) => {
        const next = { ...prev };
        delete next[invitationId];
        return next;
      });
    }
  }

  async function updateMemberRole(accountId, memberId, role) {
    if (!canManageAccess) return;
    setMemberBusyById((prev) => ({ ...prev, [memberId]: true }));
    clearAccessFeedback(accountId);
    try {
      await apiRequest(
        `/api/v1/cloud-accounts/${accountId}/members/${memberId}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ role }),
        },
        authToken,
      );
      setAccessMessageByAccount((prev) => ({ ...prev, [accountId]: 'Member role updated.' }));
      await loadAccountAccess(accountId);
    } catch (err) {
      setAccessErrorByAccount((prev) => ({
        ...prev,
        [accountId]: err.message || 'Could not update member role.',
      }));
    } finally {
      setMemberBusyById((prev) => {
        const next = { ...prev };
        delete next[memberId];
        return next;
      });
    }
  }

  async function removeMember(accountId, member) {
    if (!canManageAccess) return;
    if (member.user_id === currentUser?.id) return;

    if (!window.confirm(`Remove ${member.email} from this account?`)) {
      return;
    }

    setMemberBusyById((prev) => ({ ...prev, [member.id]: true }));
    clearAccessFeedback(accountId);
    try {
      await apiRequest(
        `/api/v1/cloud-accounts/${accountId}/members/${member.id}`,
        { method: 'DELETE' },
        authToken,
      );
      setAccessMessageByAccount((prev) => ({ ...prev, [accountId]: 'Member removed from account.' }));
      await loadAccountAccess(accountId);
    } catch (err) {
      setAccessErrorByAccount((prev) => ({
        ...prev,
        [accountId]: err.message || 'Could not remove member.',
      }));
    } finally {
      setMemberBusyById((prev) => {
        const next = { ...prev };
        delete next[member.id];
        return next;
      });
    }
  }

  function openGuidedSetupFromErrorGuide() {
    if (!errorGuide.account) return;
    setCloudAccountForm((prev) => ({
      ...prev,
      provider: errorGuide.account.provider,
      account_id: errorGuide.account.account_id || '',
      role_arn: errorGuide.account.role_arn || '',
      external_id: errorGuide.account.external_id || '',
    }));
    openGuidedSetup(errorGuide.account.provider);
    closeErrorGuide();
  }

  function openGuidedSetupFromGcpTelemetryGuide() {
    if (!gcpTelemetryGuide.account) return;
    setCloudAccountForm((prev) => ({
      ...prev,
      provider: 'gcp',
      account_id: gcpTelemetryGuide.account.account_id || '',
      role_arn: gcpTelemetryGuide.account.role_arn || '',
      external_id: '',
    }));
    openGuidedSetup('gcp');
    closeGcpTelemetryGuide();
  }

  function buildGcpTelemetryGuidePayload(account, lastSync) {
    const projectId = account?.account_id || '<project-id>';
    const issues = lastSync?.issues || [];
    const hasPermissionIssue = issues.some((code) =>
      ['gcp_no_credentials', 'gcp_project_id_missing', 'gcp_client_library_unavailable'].includes(code)
      || code.startsWith('gcp_scan_failed:'),
    );

    if (hasPermissionIssue) {
      return {
        title: 'Fix GCP scan access and permissions',
        subtitle: `Sroik cannot fully scan project ${projectId}. Apply the access steps below, then run Sync now.`,
        steps: [
          'Open Guided Setup for this project and confirm the Sroik service account email.',
          'Grant roles/browser and roles/cloudasset.viewer on the project.',
          'Grant roles/billing.viewer on the billing account used by this project.',
          'Ensure backend runtime uses ADC or Workload Identity with access to that service account.',
          'Run Sync now and verify Last error is cleared.',
        ],
      };
    }

    return {
      title: 'GCP telemetry scope in this build',
      subtitle: `Project ${projectId} is connected for inventory and cost scans. CPU/IO telemetry-based rightsizing is not implemented for GCP in this build.`,
      steps: [
        'Use this account for inventory, idle asset, and cost visibility recommendations.',
        'CPU/IO telemetry-based rightsizing is currently unavailable for GCP in this build.',
        'Keep GCP IAM grants in place so your inventory and billing coverage stays complete.',
      ],
    };
  }

  function buildErrorGuidePayload(account, issueCode) {
    const provider = account?.provider || 'aws';
    const providerLabel = getProviderLabel(provider);
    const accountLabel = `${providerLabel} account ${account?.account_id || ''}`;

    if (issueCode === 'aws_no_credentials') {
      return {
        title: 'AWS credentials are missing',
        subtitle: `Sroik cannot authenticate to scan ${accountLabel}.`,
        steps: [
          'Open Guided Setup and choose the recommended path.',
          'Create/update a cross-account IAM role with read-only and billing read permissions.',
          'Set trust to the Sroik scanner principal ARN and require the External ID shown in setup.',
          'Save credentials and run Sync now again.',
        ],
      };
    }

    if (issueCode === 'gcp_no_credentials') {
      return {
        title: 'GCP credentials are missing',
        subtitle: `Sroik cannot authenticate to scan ${accountLabel}.`,
        steps: [
          'Open Guided Setup and choose the recommended path.',
          'Grant browser, Cloud Asset viewer, and billing viewer roles to the Sroik service account.',
          'Configure backend runtime authentication using ADC or Workload Identity.',
          'Save credentials and run Sync now again.',
        ],
      };
    }

    if (issueCode === 'aws_role_arn_invalid_format') {
      return {
        title: 'AWS role ARN format is invalid',
        subtitle: 'The current ARN appears to be a user ARN instead of a role ARN.',
        steps: [
          'Edit credentials for this account.',
          'Set Role ARN to arn:aws:iam::<account-id>:role/<role-name>.',
          'Ensure the role trust policy allows the Sroik scanner principal.',
          'Save and run Sync now again.',
        ],
      };
    }

    return {
      title: 'Cloud sync failed',
      subtitle: `The last sync for ${accountLabel} returned an error.`,
      steps: [
        'Review the connected account credentials.',
        'Verify IAM permissions and trust policy for your provider.',
        'Use Guided Setup to re-apply recommended values.',
        'Run Sync now again and confirm status updates.',
      ],
    };
  }

  function hasDuplicateCloudAccount() {
    const provider = (cloudAccountForm.provider || '').trim().toLowerCase();
    const accountId = (cloudAccountForm.account_id || '').trim();
    if (!provider || !accountId) return false;
    return (cloudAccounts || []).some((account) => {
      return (
        (account.provider || '').trim().toLowerCase() === provider &&
        (account.account_id || '').trim() === accountId
      );
    });
  }

  const duplicateAccount = hasDuplicateCloudAccount();

  async function onBulkImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    setBulkImportBusy(true);
    setBulkImportError('');
    setBulkImportResult(null);
    try {
      const result = await handleBulkImport(file);
      setBulkImportResult(result);
    } catch (err) {
      setBulkImportError(err.message || 'Bulk import failed.');
    } finally {
      setBulkImportBusy(false);
      if (bulkFileRef.current) bulkFileRef.current.value = '';
    }
  }

  function formatSyncTimestamp(value) {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleString(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    });
  }

  function buildTelemetryVisibility(account, lastSync) {
    if (!lastSync) return '';
    const provider = (account?.provider || '').toLowerCase();
    const issues = lastSync?.issues || [];

    if (provider === 'gcp') {
      if (issues.includes('gcp_no_credentials')) {
        return 'Telemetry visibility: GCP inventory/cost scan is limited because runtime credentials are missing. Resolve by configuring ADC or Workload Identity for backend, then click Sync now.';
      }
      if (issues.includes('gcp_project_id_missing')) {
        return 'Telemetry visibility: GCP scan is limited because project ID is missing. Resolve by updating the account project ID, then click Sync now.';
      }
      if (issues.includes('gcp_client_library_unavailable')) {
        return 'Telemetry visibility: GCP scan is limited because server client libraries are missing. Resolve by deploying google-auth and google-api-python-client, then click Sync now.';
      }
      if (issues.some((code) => code.startsWith('gcp_scan_failed:'))) {
        return 'Telemetry visibility: GCP scan failed in the last run. Resolve IAM/API access from Last error, then click Sync now.';
      }
      return 'Telemetry scope: GCP inventory + cost only. CPU/IO telemetry-based rightsizing is not available yet in this build.';
    }

    if (provider === 'ibmcloud') {
      if (issues.includes('ibmcloud_monitoring_not_configured') || issues.includes('ibmcloud_monitoring_token_unavailable')) {
        return 'Telemetry visibility: CPU/IO checks are not active until IBM monitoring endpoint/token is configured.';
      }
      return 'Telemetry visibility: CPU and disk IOPS checks ran in this sync.';
    }

    if (['aws', 'azure', 'oracle'].includes(provider)) {
      return 'Telemetry visibility: CPU and disk IOPS checks ran in this sync.';
    }

    return 'Telemetry visibility: scan completed.';
  }

  return (
    <div className="page-view">
      <div className="page-header">
        <p className="feature-eyebrow">Cloud Accounts</p>
        <h2>Account management</h2>
        <p className="page-subtitle">Connect AWS, GCP, OCI, Azure, or IBM Cloud accounts, then manage which workspace users can access each one.</p>
      </div>

      <div className="workspace-block">
        <h4>Add cloud account</h4>
        <div className="form-actions">
          <button
            className="primary-action"
            type="button"
            onClick={() => openGuidedSetup('aws')}
            disabled={cloudSyncBusy}
          >
            Add AWS account
          </button>
          <button
            className="primary-action"
            type="button"
            onClick={() => openGuidedSetup('gcp')}
            disabled={cloudSyncBusy}
          >
            Add GCP account
          </button>
          <button
            className="primary-action"
            type="button"
            onClick={() => openGuidedSetup('oracle')}
            disabled={cloudSyncBusy}
          >
            Add OCI account
          </button>
          <button
            className="primary-action"
            type="button"
            onClick={() => openGuidedSetup('azure')}
            disabled={cloudSyncBusy}
          >
            Add Azure account
          </button>
          <button
            className="primary-action"
            type="button"
            onClick={() => openGuidedSetup('ibmcloud')}
            disabled={cloudSyncBusy}
          >
            Add IBM Cloud account
          </button>
          <button
            className="secondary-action"
            type="button"
            onClick={handleSyncAllAccounts}
            disabled={cloudSyncBusy || cloudSyncAllBusy || cloudAccounts.length === 0}
          >
            {cloudSyncAllBusy ? 'Sync queued…' : 'Sync all accounts'}
          </button>
        </div>
        {cloudSyncError && <p className="auth-feedback auth-error">{cloudSyncError}</p>}
        {cloudSyncMessage && !cloudSyncError && <p className="auth-feedback auth-success">{cloudSyncMessage}</p>}
      </div>

      <div className="workspace-block">
        <h4>Bulk import accounts</h4>
        <p className="page-subtitle">
          Download the CSV template, fill in your accounts, then upload it here. Up to 200 rows per file.
        </p>
        <div className="form-actions">
          <a
            className="secondary-action"
            href="/api/v1/cloud-accounts/bulk-import/template"
            download="sroik_cloud_accounts_template.csv"
          >
            Download CSV template
          </a>
          <label className={`primary-action${bulkImportBusy ? ' disabled' : ''}`} style={{ cursor: 'pointer' }}>
            {bulkImportBusy ? 'Importing…' : 'Upload CSV'}
            <input
              ref={bulkFileRef}
              type="file"
              accept=".csv,text/csv,text/plain"
              style={{ display: 'none' }}
              disabled={bulkImportBusy}
              onChange={onBulkImport}
            />
          </label>
        </div>
        {bulkImportError && <p className="auth-feedback auth-error">{bulkImportError}</p>}
        {bulkImportResult && (
          <div className="bulk-result">
            <p className="bulk-summary">
              Import complete — <strong>{bulkImportResult.created}</strong> created,{' '}
              <strong>{bulkImportResult.skipped}</strong> skipped,{' '}
              <strong>{bulkImportResult.errors}</strong> errors.
            </p>
            {bulkImportResult.rows && bulkImportResult.rows.length > 0 && (
              <table className="data-table bulk-table">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Provider</th>
                    <th>Account ID</th>
                    <th>Result</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkImportResult.rows.map((row) => (
                    <tr key={`${row.row}-${row.account_id}`} className={`bulk-row-${row.status}`}>
                      <td>{row.row}</td>
                      <td>{row.provider}</td>
                      <td>{row.account_id}</td>
                      <td>
                        <span className={`badge badge-${row.status}`}>{row.status}</span>
                      </td>
                      <td className="bulk-detail">{row.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {cloudAccounts.length > 0 ? (
        <div className="workspace-block">
          <h4>Connected accounts</h4>
          <ul className="card-list compact-list connected-accounts-list">
            {cloudAccounts.map((account) => (
              <li key={account.id}>
                {(() => {
                  const lastSync = cloudSyncLastByAccount?.[account.id] || account.last_sync;
                  const lastSyncAt = formatSyncTimestamp(lastSync?.synced_at);
                  const issueCode = (lastSync?.issues || [])[0] || '';
                  const telemetryVisibility = buildTelemetryVisibility(account, lastSync);
                  const issueDetails = describeIssue(issueCode);
                  return (
                <div className="account-row">
                  <div className="account-details">
                    <strong className="account-identity">{getProviderLabel(account.provider)} | {account.account_id}</strong>
                    <span className="account-status">Status: {account.status}</span>
                    {account.role_arn && <span className="account-hint">{account.role_arn}</span>}
                    {lastSync && (
                      <span className="account-sync-summary">
                        Last sync: scanned {lastSync.resources_scanned}, created {lastSync.recommendations_created}, updated {lastSync.recommendations_updated}{lastSyncAt ? ` at ${lastSyncAt}` : ''}.
                      </span>
                    )}
                    {telemetryVisibility && (
                      <span className="account-sync-summary">
                        {telemetryVisibility}
                        {(account.provider || '').toLowerCase() === 'gcp' && (
                          <>
                            {' '}
                            <button
                              type="button"
                              className="account-error-link"
                              onClick={() => openGcpTelemetryGuide(account, lastSync)}
                            >
                              Fix guidance
                            </button>
                          </>
                        )}
                      </span>
                    )}
                    {!!issueCode && (
                      <span className="account-sync-error">
                        <button
                          type="button"
                          className="account-error-link"
                          onClick={() => openErrorGuide(account, issueCode, issueDetails)}
                        >
                          Last error: {issueDetails.message}
                        </button>
                        {issueDetails.hint ? (
                          <button
                            type="button"
                            className="account-error-link"
                            onClick={() => openErrorGuide(account, issueCode, issueDetails)}
                          >
                            Hint: {issueDetails.hint}
                          </button>
                        ) : null}
                      </span>
                    )}
                  </div>
                  <div className="account-actions">
                    <button
                      className="secondary-action inline-action"
                      type="button"
                      onClick={() => handleSyncOneAccount(account.id)}
                      disabled={cloudSyncBusy || !!cloudSyncByAccount[account.id]}
                    >
                      {cloudSyncByAccount[account.id] ? 'Syncing...' : 'Sync now'}
                    </button>
                    <button
                      className="secondary-action inline-action"
                      type="button"
                      onClick={() => editingId === account.id ? setEditingId(null) : startEdit(account)}
                      disabled={cloudSyncBusy || !!cloudSyncByAccount[account.id]}
                    >
                      {editingId === account.id ? 'Cancel' : 'Edit credentials'}
                    </button>
                    <button
                      className="secondary-action inline-action"
                      type="button"
                      onClick={() => toggleAccessPanel(account.id)}
                      disabled={cloudSyncBusy || !!cloudSyncByAccount[account.id]}
                    >
                      {openAccessAccountId === account.id ? 'Hide access' : 'Manage access'}
                    </button>
                    <button
                      className="secondary-action inline-action danger-action"
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Remove ${getProviderLabel(account.provider)} account ${account.account_id}? This will also delete all its recommendations.`)) {
                          handleDeleteAccount(account.id);
                        }
                      }}
                      disabled={cloudSyncBusy || !!cloudSyncByAccount[account.id]}
                    >
                      Remove
                    </button>
                  </div>
                </div>
                  );
                })()}
                {cloudSyncByAccount[account.id] && (
                  <div className="sync-progress account-sync-progress">
                    <span className="spinner" aria-hidden="true" />
                    <span>{getProviderLabel(account.provider)} sync running...</span>
                  </div>
                )}
                {editingId === account.id && (
                  <form className="auth-form inline-form edit-credentials-form" onSubmit={(e) => submitEdit(e, account.id)}>
                    {(() => {
                      const meta = credentialEditorMeta(account.provider);
                      return (
                        <>
                    <div className="form-row">
                      <label>
                        Provider
                        <input value={getProviderLabel(account.provider)} type="text" readOnly />
                      </label>
                      <label>
                        Account ID
                        <input value={account.account_id} type="text" readOnly />
                      </label>
                    </div>
                    <label>
                      {meta.primaryLabel}
                      <input
                        value={editForm.role_arn}
                        onChange={(e) => setEditForm({ ...editForm, role_arn: e.target.value })}
                        type="text"
                        placeholder={meta.primaryPlaceholder}
                      />
                      <span className="form-hint">{meta.primaryHint}</span>
                    </label>
                    {meta.externalIdEnabled && (
                      <label>
                        External ID
                        <input
                          value={editForm.external_id}
                          onChange={(e) => setEditForm({ ...editForm, external_id: e.target.value })}
                          type="text"
                          placeholder="Optional"
                        />
                        <span className="form-hint">{meta.externalIdHint}</span>
                      </label>
                    )}
                    <div className="form-actions">
                      <button
                        className="secondary-action inline-action"
                        type="button"
                        onClick={() => setEditingId(null)}
                        disabled={cloudSyncBusy}
                      >
                        Cancel
                      </button>
                      <button className="primary-action" type="submit" disabled={cloudSyncBusy}>Save</button>
                    </div>
                        </>
                      );
                    })()}
                  </form>
                )}
                {openAccessAccountId === account.id && (
                  <div className="auth-form inline-form" style={{ marginTop: 12 }}>
                    <h5>Account members</h5>
                    {accessBusyByAccount[account.id] ? (
                      <p className="form-hint">Loading access…</p>
                    ) : (
                      <ul className="card-list compact-list">
                        {(membersByAccount[account.id] || []).map((member) => (
                          <li key={member.id}>
                            <div className="account-row">
                              <div>
                                <strong>{member.full_name || member.email}</strong>
                                <span className="account-hint">{member.email}</span>
                              </div>
                              <div className="account-actions">
                                {canManageAccess ? (
                                  <>
                                    <select
                                      className="input-sm"
                                      value={member.role}
                                      disabled={!!memberBusyById[member.id]}
                                      onChange={(e) => updateMemberRole(account.id, member.id, e.target.value)}
                                    >
                                      <option value="viewer">viewer</option>
                                      <option value="analyst">analyst</option>
                                      <option value="admin">admin</option>
                                    </select>
                                    <button
                                      className="secondary-action inline-action danger-action"
                                      type="button"
                                      disabled={!canRemoveMembers || !!memberBusyById[member.id] || member.user_id === currentUser?.id}
                                      onClick={() => removeMember(account.id, member)}
                                    >
                                      {memberBusyById[member.id] ? 'Working…' : 'Remove'}
                                    </button>
                                  </>
                                ) : (
                                  <span className="badge badge-ready">{member.role}</span>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                        {(membersByAccount[account.id] || []).length === 0 && (
                          <li>
                            <p className="form-hint">No members yet.</p>
                          </li>
                        )}
                      </ul>
                    )}

                    {canManageAccess && (
                      <>
                        <h5>Invite member</h5>
                        <form onSubmit={(e) => submitInvitation(e, account.id)}>
                          <label>
                            User email
                            <input
                              type="email"
                              list="tenant-user-emails"
                              value={getInviteForm(account.id).invited_email}
                              onChange={(e) => updateInviteForm(account.id, { invited_email: e.target.value })}
                              placeholder="user@company.com"
                              required
                            />
                          </label>
                          <div className="form-row">
                            <label>
                              Role
                              <select
                                value={getInviteForm(account.id).invited_role}
                                onChange={(e) => updateInviteForm(account.id, { invited_role: e.target.value })}
                              >
                                <option value="viewer">viewer</option>
                                <option value="analyst">analyst</option>
                                <option value="admin">admin</option>
                              </select>
                            </label>
                            <label>
                              Expires (days)
                              <input
                                type="number"
                                min="1"
                                max="30"
                                value={getInviteForm(account.id).expires_in_days}
                                onChange={(e) => updateInviteForm(account.id, { expires_in_days: e.target.value })}
                              />
                            </label>
                          </div>
                          <div className="form-actions">
                            <button
                              className="primary-action"
                              type="submit"
                              disabled={inviteBusyByAccount[account.id]}
                            >
                              {inviteBusyByAccount[account.id] ? 'Inviting…' : 'Create invitation'}
                            </button>
                          </div>
                        </form>

                        <h5>Invitations</h5>
                        <ul className="card-list compact-list">
                          {(invitationsByAccount[account.id] || []).map((invitation) => (
                            <li key={invitation.id}>
                              <div className="account-row">
                                <div>
                                  <strong>{invitation.invited_email}</strong>
                                  <span className="account-hint">
                                    role {invitation.invited_role} | status {invitation.status_label || invitation.status} | expires {formatSyncTimestamp(invitation.expires_at)}
                                  </span>
                                </div>
                                <div className="account-actions">
                                  {['pending', 'expired'].includes(invitation.status) && (
                                    <button
                                      className="secondary-action inline-action"
                                      type="button"
                                      disabled={!!resendBusyByInvitation[invitation.id]}
                                      onClick={() => resendInvitation(account.id, invitation.id, 'resend')}
                                    >
                                      {resendBusyByInvitation[invitation.id] ? 'Resending…' : 'Resend'}
                                    </button>
                                  )}
                                  {invitation.status === 'revoked' && (
                                    <button
                                      className="secondary-action inline-action"
                                      type="button"
                                      disabled={!!resendBusyByInvitation[invitation.id]}
                                      onClick={() => resendInvitation(account.id, invitation.id, 'reinvite')}
                                    >
                                      {resendBusyByInvitation[invitation.id] ? 'Re-inviting…' : 'Re-invite'}
                                    </button>
                                  )}
                                  {invitation.status === 'pending' && (
                                    <button
                                      className="secondary-action inline-action danger-action"
                                      type="button"
                                      disabled={!!revokeBusyByInvitation[invitation.id]}
                                      onClick={() => revokeInvitation(account.id, invitation.id)}
                                    >
                                      {revokeBusyByInvitation[invitation.id] ? 'Revoking…' : 'Revoke'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </li>
                          ))}
                          {(invitationsByAccount[account.id] || []).length === 0 && (
                            <li>
                              <p className="form-hint">No invitations for this account.</p>
                            </li>
                          )}
                        </ul>
                      </>
                    )}

                    {accessErrorByAccount[account.id] && (
                      <p className="auth-feedback auth-error">{accessErrorByAccount[account.id]}</p>
                    )}
                    {accessMessageByAccount[account.id] && !accessErrorByAccount[account.id] && (
                      <p className="auth-feedback auth-success">{accessMessageByAccount[account.id]}</p>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="empty-state">
          <p className="feature-eyebrow">No accounts connected</p>
          <p>Add your first cloud account above to start generating recommendations.</p>
        </div>
      )}

      {showSetupModal && (
        <div className="setup-modal-overlay" onClick={() => setShowSetupModal(false)}>
          <div className="setup-modal" onClick={(e) => e.stopPropagation()}>
            <div className="setup-modal-header">
              <h4>{`Guided ${getProviderLabel(cloudAccountForm.provider)} Setup`}</h4>
              <button className="secondary-action inline-action" type="button" onClick={() => setShowSetupModal(false)}>Close</button>
            </div>

            {cloudBootstrapBusy && (
              <div className="sync-progress">
                <span className="spinner" aria-hidden="true" />
                <span>Loading setup guidance...</span>
              </div>
            )}

            <form className="auth-form inline-form" onSubmit={submitGuidedSetup}>

              <div className="auth-tabs">
                <button
                  className={setupMode === 'recommended' ? 'auth-tab active' : 'auth-tab'}
                  type="button"
                  onClick={() => setSetupMode('recommended')}
                >
                  Recommended path
                </button>
                <button
                  className={setupMode === 'manual' ? 'auth-tab active' : 'auth-tab'}
                  type="button"
                  onClick={() => setSetupMode('manual')}
                >
                  Manual path
                </button>
              </div>

              {setupMode === 'recommended' && (
                <div className="setup-guide setup-guide-strong">
                  <p className="feature-eyebrow">Single admin approval</p>
                  {cloudBootstrap?.summary && <p>{cloudBootstrap.summary}</p>}
                  {cloudBootstrap?.caution && <p className="form-hint">{cloudBootstrap.caution}</p>}
                  {cloudBootstrapError && <p className="auth-feedback auth-error">{cloudBootstrapError}</p>}
                  {copyStatus && <p className="form-hint">{copyStatus}</p>}

                  {cloudBootstrap && (
                    <>
                      <div className="setup-meta-grid">
                        {cloudBootstrap.provider === 'aws' && cloudBootstrap.role_name && (
                          <div className="setup-meta-card">
                            <span>Recommended role</span>
                            <strong>{cloudBootstrap.role_name}</strong>
                          </div>
                        )}
                        {cloudBootstrap.provider === 'aws' && cloudBootstrap.external_id && (
                          <div className="setup-meta-card">
                            <span>External ID</span>
                            <strong>{cloudBootstrap.external_id}</strong>
                          </div>
                        )}
                        {cloudBootstrap.provider === 'aws' && cloudBootstrap.aws_trust_principal_arn && (
                          <div className="setup-meta-card">
                            <span>Trust principal</span>
                            <strong>{cloudBootstrap.aws_trust_principal_arn}</strong>
                          </div>
                        )}
                        {cloudBootstrap.provider === 'aws' && !cloudBootstrap.aws_trust_principal_arn && (
                          <div className="setup-meta-card">
                            <span>Trust principal</span>
                            <strong>Temporarily unavailable. Contact Sroik support.</strong>
                          </div>
                        )}
                        {cloudBootstrap.provider === 'gcp' && cloudBootstrap.gcp_service_account_email && (
                          <div className="setup-meta-card">
                            <span>Sroik service account</span>
                            <strong>{cloudBootstrap.gcp_service_account_email}</strong>
                          </div>
                        )}
                      </div>

                      {cloudBootstrap.provider === 'aws' && buildRecommendedRoleArn() && (
                        <div className="setup-preview">
                          <span>Role ARN preview</span>
                          <code>{buildRecommendedRoleArn()}</code>
                        </div>
                      )}

                      <ul className="card-list compact-list setup-bullets">
                        {cloudBootstrap.permission_bullets.map((bullet) => (
                          <li key={bullet}>{bullet}</li>
                        ))}
                      </ul>
                      <ol>
                        {cloudBootstrap.setup_steps.map((step) => (
                          <li key={step}>{step}</li>
                        ))}
                      </ol>
                      <div className="form-actions">
                        <a className="secondary-action" href={cloudBootstrap.console_url} target="_blank" rel="noreferrer">
                          Open provider console
                        </a>
                        <button className="secondary-action" type="button" onClick={autoFillMissingFields} disabled={!canAutoFillMissingFields()}>
                          Auto-fill missing fields
                        </button>
                        <button className="secondary-action" type="button" onClick={applyRecommendedValues}>
                          Apply recommended values
                        </button>
                      </div>

                      {cloudBootstrap.provider === 'aws' ? (
                        <div className="setup-snippet-grid">
                          <div className="setup-snippet">
                            <div className="setup-snippet-header">
                              <span>Trust policy</span>
                              <button
                                className="secondary-action inline-action"
                                type="button"
                                onClick={() => handleCopy('Trust policy', buildAwsTrustPolicy())}
                                disabled={!hasAwsTrustPrincipal()}
                              >
                                {hasAwsTrustPrincipal() ? 'Copy trust policy' : 'Copy unavailable'}
                              </button>
                            </div>
                            {!hasAwsTrustPrincipal() && (
                              <p className="form-hint">
                                Trust principal is temporarily unavailable for this tenant. Please contact Sroik
                                support to complete platform readiness.
                              </p>
                            )}
                            {hasAwsTrustPrincipal() && !cloudAccountForm.external_id.trim() && cloudBootstrap?.external_id && (
                              <p className="form-hint">
                                External ID is live and will use the generated value unless you override it below.
                              </p>
                            )}
                            <pre>{buildAwsTrustPolicy()}</pre>
                          </div>
                          <div className="setup-snippet">
                            <div className="setup-snippet-header">
                              <span>Billing read policy</span>
                              <button className="secondary-action inline-action" type="button" onClick={() => handleCopy('Billing read policy', buildAwsBillingPolicy())}>
                                Copy billing policy
                              </button>
                            </div>
                            <pre>{buildAwsBillingPolicy()}</pre>
                          </div>
                        </div>
                      ) : cloudBootstrap.provider === 'gcp' ? (
                        <>
                          <div className="setup-snippet">
                            <div className="setup-snippet-header">
                              <span>Sroik Service Account Email</span>
                              <button className="secondary-action inline-action" type="button" onClick={() => handleCopy('Sroik service account email', `serviceAccount:${cloudBootstrap?.gcp_service_account_email || DEFAULT_GCP_SCANNER_SERVICE_ACCOUNT}`)}>
                                Copy service account
                              </button>
                            </div>
                            <pre>{`serviceAccount:${cloudBootstrap?.gcp_service_account_email || DEFAULT_GCP_SCANNER_SERVICE_ACCOUNT}`}</pre>
                            <p className="form-hint">Grant this service account the required IAM roles in your GCP project and billing accounts.</p>
                          </div>
                          <div className="setup-snippet">
                            <div className="setup-snippet-header">
                              <span>GCP IAM grant commands</span>
                              <button className="secondary-action inline-action" type="button" onClick={() => handleCopy('GCP IAM commands', buildGcpCommands())}>
                                Copy gcloud commands
                              </button>
                            </div>
                            <pre>{buildGcpCommands()}</pre>
                          </div>
                        </>
                      ) : cloudBootstrap.provider === 'azure' ? (
                        <div className="setup-snippet">
                          <div className="setup-snippet-header">
                            <span>Azure role assignment commands</span>
                            <button className="secondary-action inline-action" type="button" onClick={() => handleCopy('Azure setup commands', buildAzureCommands())}>
                              Copy az commands
                            </button>
                          </div>
                          <pre>{buildAzureCommands()}</pre>
                          <p className="form-hint">Assign Reader, Monitoring Reader, and Cost Management Reader at the subscription scope.</p>
                        </div>
                      ) : cloudBootstrap.provider === 'oracle' ? (
                        <div className="setup-snippet">
                          <div className="setup-snippet-header">
                            <span>OCI policy statements</span>
                            <button className="secondary-action inline-action" type="button" onClick={() => handleCopy('OCI policy statements', buildOracleCommands())}>
                              Copy OCI policies
                            </button>
                          </div>
                          <pre>{buildOracleCommands()}</pre>
                          <p className="form-hint">Apply these policies to the scanner dynamic group or user group at tenancy or compartment scope.</p>
                        </div>
                      ) : cloudBootstrap.provider === 'ibmcloud' ? (
                        <div className="setup-snippet">
                          <div className="setup-snippet-header">
                            <span>IBM Cloud setup commands</span>
                            <button className="secondary-action inline-action" type="button" onClick={() => handleCopy('IBM Cloud setup commands', buildIbmCloudCommands())}>
                              Copy ibmcloud commands
                            </button>
                          </div>
                          <pre>{buildIbmCloudCommands()}</pre>
                          <p className="form-hint">Grant viewer-level account, VPC, billing, and monitoring access for scanner operations.</p>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              )}

              {setupMode === 'manual' && cloudAccountForm.provider === 'aws' ? (
                <div className="setup-guide">
                  <p className="feature-eyebrow">AWS Setup Steps</p>
                  <ol>
                    <li>Sign in to AWS Console and open IAM Roles.</li>
                    <li>Create a role for cross-account access and attach read-only policies.</li>
                    <li>Trust your platform scanner identity to assume this role.</li>
                    <li>Copy AWS account ID and role ARN into fields below.</li>
                  </ol>
                  <a href="https://console.aws.amazon.com/iam/home#/roles" target="_blank" rel="noreferrer">Open AWS IAM Roles</a>
                </div>
              ) : null}

              {setupMode === 'manual' && cloudAccountForm.provider === 'gcp' ? (
                <div className="setup-guide">
                  <p className="feature-eyebrow">GCP Setup Steps</p>
                  <ol>
                    <li>Open Google Cloud Console and select your project.</li>
                    <li>Create a service account with viewer permissions for billing/resource inventory.</li>
                    <li>Configure ADC / Workload Identity for backend runtime.</li>
                    <li>Copy project ID and service-account hint into fields below.</li>
                  </ol>
                  <a href="https://console.cloud.google.com/iam-admin/serviceaccounts" target="_blank" rel="noreferrer">Open GCP Service Accounts</a>
                </div>
              ) : null}

              {setupMode === 'manual' && !['aws', 'gcp'].includes(cloudAccountForm.provider) ? (
                <div className="setup-guide">
                  <p className="feature-eyebrow">Provider setup steps</p>
                  <ol>
                    <li>Open your cloud provider IAM/identity console.</li>
                    <li>Create scanner credentials with read-only inventory, billing, and monitoring access.</li>
                    <li>Set account identifier and optional credential hints in the fields below.</li>
                    <li>Save the account and run Sync now to validate scanner access.</li>
                  </ol>
                  {cloudBootstrap?.console_url && (
                    <a href={cloudBootstrap.console_url} target="_blank" rel="noreferrer">Open provider console</a>
                  )}
                </div>
              ) : null}

              <label>
                Account ID / Project ID
                <input
                  value={cloudAccountForm.account_id}
                  onChange={(e) => handleAccountIdChange(e.target.value)}
                  type="text"
                  required
                />
              </label>
              {cloudAccountForm.provider === 'aws' && (
                <>
                  <label>
                    Role ARN
                    <input
                      value={cloudAccountForm.role_arn}
                      onChange={(e) => setCloudAccountForm({ ...cloudAccountForm, role_arn: e.target.value })}
                      type="text"
                      placeholder="arn:aws:iam::123456789012:role/SroikReadOnly"
                    />
                  </label>
                  <label>
                    External ID
                    <input
                      value={cloudAccountForm.external_id}
                      onChange={(e) => setCloudAccountForm({ ...cloudAccountForm, external_id: e.target.value })}
                      type="text"
                      placeholder="Recommended for confused-deputy protection"
                    />
                  </label>
                </>
              )}
              <p className="form-hint">
                Save the account after IAM approval. Resource sync remains read-only and can be triggered separately.
              </p>
              {duplicateAccount && (
                <p className="auth-feedback auth-error">
                  This account is already connected for this provider.
                </p>
              )}
              <div className="setup-snippet">
                <div className="setup-snippet-header">
                  <span>Live connection payload</span>
                </div>
                <pre>{buildCloudAccountPayloadPreview()}</pre>
              </div>
              <div className="form-actions">
                <button className="primary-action" type="submit" disabled={cloudSyncBusy || duplicateAccount}>
                  {cloudSyncBusy ? (
                    <>
                      <span className="spinner" aria-hidden="true" />
                      <span>&nbsp;Connecting...</span>
                    </>
                  ) : (
                    'Connect account'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {errorGuide.open && (
        <div className="setup-modal-overlay" onClick={closeErrorGuide}>
          <div className="setup-modal setup-help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="setup-modal-header">
              <h4>{buildErrorGuidePayload(errorGuide.account, errorGuide.issueCode).title}</h4>
              <button className="secondary-action inline-action" type="button" onClick={closeErrorGuide}>Close</button>
            </div>
            <div className="setup-guide setup-guide-strong">
              <p>{buildErrorGuidePayload(errorGuide.account, errorGuide.issueCode).subtitle}</p>
              {(errorGuide.details?.message || errorGuide.details?.hint) && (
                <p className="form-hint">
                  {errorGuide.details?.message}
                  {errorGuide.details?.hint ? ` ${errorGuide.details.hint}` : ''}
                </p>
              )}
              <ol>
                {buildErrorGuidePayload(errorGuide.account, errorGuide.issueCode).steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
              <div className="form-actions">
                <button className="primary-action" type="button" onClick={openGuidedSetupFromErrorGuide}>
                  Open guided setup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {gcpTelemetryGuide.open && (
        <div className="setup-modal-overlay" onClick={closeGcpTelemetryGuide}>
          <div className="setup-modal setup-help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="setup-modal-header">
              <h4>{buildGcpTelemetryGuidePayload(gcpTelemetryGuide.account, gcpTelemetryGuide.lastSync).title}</h4>
              <button className="secondary-action inline-action" type="button" onClick={closeGcpTelemetryGuide}>Close</button>
            </div>
            <div className="setup-guide setup-guide-strong">
              <p>{buildGcpTelemetryGuidePayload(gcpTelemetryGuide.account, gcpTelemetryGuide.lastSync).subtitle}</p>
              <ol>
                {buildGcpTelemetryGuidePayload(gcpTelemetryGuide.account, gcpTelemetryGuide.lastSync).steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
              <div className="setup-snippet">
                <div className="setup-snippet-header">
                  <span>GCP IAM grant commands</span>
                  <button
                    className="secondary-action inline-action"
                    type="button"
                    onClick={() => handleCopy('GCP IAM commands', buildGcpCommandsForProject(gcpTelemetryGuide.account?.account_id))}
                  >
                    Copy gcloud commands
                  </button>
                </div>
                <pre>{buildGcpCommandsForProject(gcpTelemetryGuide.account?.account_id)}</pre>
              </div>
              <div className="form-actions">
                <button className="primary-action" type="button" onClick={openGuidedSetupFromGcpTelemetryGuide}>
                  Open guided setup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <datalist id="tenant-user-emails">
        {tenantUsers.map((user) => (
          <option key={user.id} value={user.email}>
            {user.full_name ? `${user.full_name} (${user.email})` : user.email}
          </option>
        ))}
      </datalist>
    </div>
  );
}
